import { isPlainObject, toLower } from 'lodash';
import { isNotNullOrUndefined, isNullOrUndefined, isStringNotEmpty } from './objectUtils';

const REDACTED_VALUE = '***REDACTED***';

const SENSITIVE_FIELD_NAMES = [
    'ccnumber',
    'cardnumber',
    'creditcardnumber',
    'paymentaccountnumber',
    'cvv',
    'cvc',
    'cvv2',
    'cvc2',
    'securitycode',
    'cardsecuritycode',
    'ccexp',
    'cardexp',
    'expirationdate',
    'expirydate',
    'expdate',
    'expiry',
    'paymenttoken',
    'applepaytoken',
    'googlepaytoken',
    'accountnumber',
    'routingnumber'
];

const SENSITIVE_HEADER_NAMES = [
    'authorization',
    'x-revolv3-token',
    'cookie',
    'set-cookie'
];

const isSensitiveFieldName = (fieldName) => {
    return SENSITIVE_FIELD_NAMES.includes(toLower(fieldName));
};

const redactSensitiveFields = (value, depth = 0) => {
    if (depth > 10) {
        return REDACTED_VALUE;
    }

    if (Array.isArray(value)) {
        return value.map((item) => redactSensitiveFields(item, depth + 1));
    }

    // only plain objects are walked; FormData/URLSearchParams/binary values would be
    // destroyed by an entries() rebuild, so they pass through untouched
    if (isPlainObject(value)) {
        const redacted = {};
        for (const [fieldName, fieldValue] of Object.entries(value)) {
            if (isSensitiveFieldName(fieldName) && isNotNullOrUndefined(fieldValue)) {
                redacted[fieldName] = REDACTED_VALUE;
            } else {
                redacted[fieldName] = redactSensitiveFields(fieldValue, depth + 1);
            }
        }
        return redacted;
    }

    return value;
};

const containsSensitiveFieldName = (body) => {
    const lowerBody = toLower(body);
    return SENSITIVE_FIELD_NAMES.some((fieldName) => lowerBody.includes(fieldName));
};

const sanitizeNetworkBody = (body) => {
    if (Array.isArray(body) || isPlainObject(body)) {
        return redactSensitiveFields(body);
    }

    if (isStringNotEmpty(body)) {
        // exact-match redaction is a subset of this substring check, so a miss here proves there is
        // nothing to redact and the parse/rebuild/stringify cost can be skipped for the common case
        if (!containsSensitiveFieldName(body)) {
            return body;
        }

        try {
            return JSON.stringify(redactSensitiveFields(JSON.parse(body)));
        } catch (ex) {
            // a non-JSON body that names a card field cannot be selectively redacted, so drop it entirely
            return REDACTED_VALUE;
        }
    }

    return body;
};

const sanitizeNetworkHeaders = (headers) => {
    if (isNullOrUndefined(headers)) {
        return headers;
    }

    let plainHeaders = null;
    if (isPlainObject(headers)) {
        plainHeaders = { ...headers };
    } else if (typeof headers.forEach === 'function') {
        // fetch Headers / AxiosHeaders instances hide their entries from a plain-object walk
        plainHeaders = {};
        headers.forEach((headerValue, headerName) => {
            plainHeaders[headerName] = headerValue;
        });
    }

    // a headers value that cannot be enumerated could hide an auth token, so drop it entirely
    if (isNullOrUndefined(plainHeaders)) {
        return {};
    }

    for (const headerName of Object.keys(plainHeaders)) {
        if (SENSITIVE_HEADER_NAMES.includes(toLower(headerName))) {
            plainHeaders[headerName] = REDACTED_VALUE;
        }
    }

    return plainHeaders;
};

// the dataLayer push next to every LogRocket.track is a hand-written allowlist of the ~50 fields
// GTM needs; the track call is a wholesale spread of the whole SiteEvent, so it carries strictly
// more - including `bmi`, which the allowlist deliberately omits. these three are the health facts
// in that gap. `shippingState` stays: it is a marketing geo dimension GTM is already given, and
// dropping it here would cost segmentation without removing anything LogRocket cannot already
// infer from the session.
const PHI_EVENT_PROPERTY_NAMES = [
    'dateofbirth',
    'gender',
    'bmi'
];

// strips the health facts out of a LogRocket custom-event payload. this is deliberately applied to
// the track argument and not to the dataLayer object beside it: server-side GTM has no cookies, so
// its tags read the customer's ad identifiers straight off that payload and a redaction reaching it
// would silently break attribution
export const sanitizeTrackProperties = (properties) => {
    if (!isPlainObject(properties)) {
        return properties;
    }

    return Object.entries(properties).reduce((sanitizedProperties, [propertyName, propertyValue]) => {
        if (!PHI_EVENT_PROPERTY_NAMES.includes(toLower(propertyName))) {
            sanitizedProperties[propertyName] = propertyValue;
        }

        return sanitizedProperties;
    }, {});
};

export const requestSanitizer = (request) => {
    if (isNotNullOrUndefined(request)) {
        request.headers = sanitizeNetworkHeaders(request.headers);
        request.body = sanitizeNetworkBody(request.body);
    }

    return request;
};

export const responseSanitizer = (response) => {
    if (isNotNullOrUndefined(response)) {
        response.headers = sanitizeNetworkHeaders(response.headers);
        response.body = sanitizeNetworkBody(response.body);
    }

    return response;
};
